import { Box, createStyles, SimpleGrid, Text, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { RpiAlert, RpiCheckboxV2 } from "@rpi/openapi-core";
import React from "react";
import { RpaButtonPromo } from "../../../components/buttons/RpaButton";
import { BraintreeInput } from "../../../components/inputFields/BraintreeInput";
import { RpaDropdown } from "../../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../../components/inputFields/RpaTextInput";
import { RpaNavLink } from "../../../components/RpiLink";
import { useBraintree } from "../../../hooks/useBraintree";
import { getRoutePath } from "../../../hooks/useNavigateRoute";
import { countrySelectData } from "../../../utils/country.util";
import { PaymentFormValues, requiredField, usePaymentFormSubmission } from "./hooks/usePaymentFormSubmission";

interface CreatPaymentMethodProps {
  onComplete: () => void;
}

const useStyles = createStyles((theme) => ({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.md,
  },
  skipLink: {
    textAlign: "center",
  },
}));

export const CreatePaymentMethod: React.FC<CreatPaymentMethodProps> = ({ onComplete }) => {
  const [countrySearchValue, setCountrySearchValue] = React.useState("");

  const { classes } = useStyles();

  const { fields, hostedFields, dataCollector, getPayload } = useBraintree();

  const form = useForm<PaymentFormValues>({
    initialValues: {
      cardholderName: "",
      streetAddress: "",
      city: "",
      state: "",
      country: "US",
      makeDefault: true,
      agreeToTerms: false,
    },

    validate: {
      cardholderName: requiredField("Please enter a valid name"),
      streetAddress: requiredField("Please enter a valid street address"),
      city: requiredField("Please enter a valid town/city"),
      state: (value: string | undefined, values: PaymentFormValues) =>
        values?.country === "US" && (value?.length ?? 0) === 0 ? "Please enter a valid state" : null,
      country: requiredField("Please select a country"),
      agreeToTerms: (value: boolean) => (value ? null : "Please agree to the payment terms to continue"),
    },
  });

  const { isLoading, handleSubmit, cardError, braintreeErrors, createPaymentMethod, onSubmit, formRef } =
    usePaymentFormSubmission({
      form,
      hostedFields,
      dataCollector,
      getPayload,
      onComplete,
    });

  return (
    <form ref={formRef} onSubmit={form.onSubmit(onSubmit)} className={classes.form}>
      <>
        <Title
          style={{
            fontSize: "24px",
            fontWeight: 600,
          }}
        >
          Let's set up your payment method
        </Title>
        <Text style={{ fontWeight: 600 }}>
          You will need to set up your payment method for the API integration. <br />
          You can also set this up later in Dashboard {">"} Account Settings.
        </Text>
        <RpaTextInput
          placeholder="Enter"
          required
          label="Name on card"
          disabled={isLoading}
          {...form.getInputProps("cardholderName")}
        />

        <BraintreeInput
          label="Credit Card number"
          isFocused={fields.number.isFocused}
          ref={fields.number.ref as any}
          onFocus={() => !isLoading && hostedFields?.focus("number")}
          error={braintreeErrors.number}
          errorMessage="Please enter a valid credit card number"
          disabled={isLoading}
        />

        <SimpleGrid cols={2} spacing={8}>
          <BraintreeInput
            label="Experation Date"
            isFocused={fields.expirationDate.isFocused}
            ref={fields.expirationDate.ref as any}
            onFocus={() => !isLoading && hostedFields?.focus("expirationDate")}
            error={!!braintreeErrors.expirationDate}
            errorMessage="Please enter a valid expiration date"
            disabled={isLoading}
          />

          <BraintreeInput
            label="CVC"
            isFocused={fields.cvv.isFocused}
            ref={fields.cvv.ref as any}
            onFocus={() => !isLoading && hostedFields?.focus("cvv")}
            error={!!braintreeErrors.cvv}
            errorMessage="Please enter a valid CVC code"
            disabled={isLoading}
          />
        </SimpleGrid>

        <BraintreeInput
          label="Postal Code"
          isFocused={fields.postalCode.isFocused}
          ref={fields.postalCode.ref as any}
          onFocus={() => !isLoading && hostedFields?.focus("postalCode")}
          error={braintreeErrors.postalCode}
          errorMessage="Please enter a valid postal code"
          disabled={isLoading}
        />
        <Box
          sx={{
            width: "100%",
            height: 1,
            background: "#D1D1D1",
            margin: "16px 0",
          }}
        />

        <RpaTextInput
          placeholder="Enter"
          required
          label="Billing Street Address"
          disabled={isLoading}
          {...form.getInputProps("streetAddress")}
        />
        <RpaTextInput
          placeholder="Enter"
          required
          label="Town/City"
          disabled={isLoading}
          {...form.getInputProps("city")}
        />
        {form.values?.country === "US" && (
          <RpaTextInput
            placeholder="Enter"
            required
            label="State"
            disabled={isLoading}
            {...form.getInputProps("state")}
          />
        )}
        <RpaDropdown
          required
          label="Country"
          data={countrySelectData}
          {...form.getInputProps("country")}
          disabled={isLoading}
          searchable
          clearable={false}
          filterDataOnExactSearchMatch
          searchValue={countrySearchValue}
          onSearchChange={setCountrySearchValue}
          nothingFound="No countries found"
        />

        <RpiCheckboxV2
          label="By saving your payment method, you agree to be charged for your orders daily."
          {...form.getInputProps("agreeToTerms", { type: "checkbox", withError: true })}
          disabled={isLoading}
        />

        {cardError && (
          <RpiAlert>The provided credit card was declined. Please try paying with a different card</RpiAlert>
        )}

        {createPaymentMethod.error && (
          <RpiAlert>There was an error while creating your payment method. Please try again later.</RpiAlert>
        )}

        <RpaButtonPromo
          disabled={!!cardError || isLoading}
          isLoading={isLoading}
          type="submit"
          onClick={hostedFields && dataCollector && handleSubmit}
        >
          Save
        </RpaButtonPromo>
        <RpaNavLink className={classes.skipLink} to={getRoutePath("home")}>
          Skip
        </RpaNavLink>
      </>
    </form>
  );
};
